/*
 * 发送给（对齐 Figma）
 *
 * 阶段 1 实现：纯前端做分享侧效果，不依赖任何三方 SDK。
 *   - 链接：navigator.clipboard 复制 → toast "链接已复制"
 *   - 邮件：window.location.href = mailto:?subject=&body=
 *   - 微信：复制 → 弹 Modal "链接已复制，请打开微信粘贴发送"
 *   - 钉钉：复制 → 弹 Modal "链接已复制，请打开钉钉粘贴发送"
 *
 * 后期要真分享（如钉钉机器人 webhook）只改对应 case 即可，UI 不动。
 */

import React from 'react'
import { message, Modal } from 'antd'

interface Target {
  key: string
  label: string
  emoji?: string
  /** 自定义色 token；不传时按 key 推断 */
  tone?: 'blue' | 'green' | 'orange' | 'neutral'
}

interface Props {
  targets: Target[]
  /** 要分享的链接（必传，否则按钮被禁用） */
  shareUrl?: string
  /** 邮件主题 */
  emailSubject?: string
  /** 卡头文案，默认 "发送给" */
  title?: string
  /** 副标题，默认 "快速把结果给到客户或团队" */
  description?: string
  /** 点击钩子（用于埋点等，副作用已在内部完成） */
  onShare?: (key: string) => void
}

const TONE_BY_KEY: Record<string, NonNullable<Target['tone']>> = {
  dingtalk: 'blue',
  钉钉: 'blue',
  wechat: 'green',
  weixin: 'green',
  微信: 'green',
  email: 'orange',
  mail: 'orange',
  邮件: 'orange',
  link: 'neutral',
  copy: 'neutral',
  链接: 'neutral'
}

const DEFAULT_EMOJI: Record<string, string> = {
  dingtalk: '🤖',
  钉钉: '🤖',
  wechat: '💬',
  weixin: '💬',
  微信: '💬',
  email: '📧',
  mail: '📧',
  邮件: '📧',
  link: '🔗',
  copy: '🔗',
  链接: '🔗'
}

function resolveTone(t: Target): NonNullable<Target['tone']> {
  return t.tone || TONE_BY_KEY[t.key] || TONE_BY_KEY[t.label] || 'neutral'
}

function resolveEmoji(t: Target): string {
  return t.emoji || DEFAULT_EMOJI[t.key] || DEFAULT_EMOJI[t.label] || '📤'
}

/** 推断渠道大类（用 key + label 双匹配，AI 给的 key 不固定） */
type Channel = 'link' | 'email' | 'wechat' | 'dingtalk' | 'unknown'

function resolveChannel(t: Target): Channel {
  const probe = `${t.key} ${t.label}`.toLowerCase()
  if (/dingtalk|钉钉/.test(probe)) return 'dingtalk'
  if (/wechat|weixin|微信/.test(probe)) return 'wechat'
  if (/email|mail|邮件/.test(probe)) return 'email'
  if (/link|copy|链接|复制/.test(probe)) return 'link'
  return 'unknown'
}

/** 复制到剪贴板，老浏览器 fallback execCommand */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* no-op */
  }
  // fallback
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

export const ShareTargets: React.FC<Props> = ({
  targets,
  shareUrl,
  emailSubject = 'AI 填表员生成的文档',
  title = '发送给',
  description = '快速把结果给到客户或团队',
  onShare
}) => {
  const handleClick = async (t: Target) => {
    const ch = resolveChannel(t)

    if (!shareUrl) {
      message.warning('暂无可分享的内容')
      return
    }

    switch (ch) {
      case 'link': {
        const ok = await copyToClipboard(shareUrl)
        if (ok) message.success('链接已复制')
        else message.error('复制失败，请手动选中复制')
        break
      }

      case 'email': {
        const subject = encodeURIComponent(emailSubject)
        const body = encodeURIComponent(
          `文档已生成，可在以下链接查看：\n${shareUrl}\n\n— 来自 AI 填表员`
        )
        // 直接用 location.href 触发系统邮件客户端
        window.location.href = `mailto:?subject=${subject}&body=${body}`
        break
      }

      case 'wechat':
      case 'dingtalk': {
        const channelName = ch === 'wechat' ? '微信' : '钉钉'
        const ok = await copyToClipboard(shareUrl)
        Modal.info({
          title: ok ? '链接已复制' : '请手动复制链接',
          content: (
            <div style={{ lineHeight: 1.7 }}>
              {ok ? (
                <>
                  打开{channelName}，<b>按 Ctrl/⌘+V</b>{' '}
                  粘贴到聊天框，发送给好友或群即可。
                </>
              ) : (
                <>
                  浏览器拒绝了复制权限，请手动复制下方链接：
                  <div
                    style={{
                      marginTop: 8,
                      padding: 8,
                      background: '#f5f5f5',
                      borderRadius: 4,
                      wordBreak: 'break-all',
                      userSelect: 'all'
                    }}
                  >
                    {shareUrl}
                  </div>
                </>
              )}
            </div>
          ),
          okText: '知道了',
          centered: true
        })
        break
      }

      default: {
        // 未知渠道兜底：复制
        const ok = await copyToClipboard(shareUrl)
        if (ok) message.success('链接已复制')
        else message.error('复制失败')
      }
    }

    if (onShare) onShare(t.key)
  }

  return (
    <div className="aiClerk-att-share">
      <div className="aiClerk-att-share__title">{title}</div>
      {description && (
        <div className="aiClerk-att-share__desc">{description}</div>
      )}
      <div className="aiClerk-att-share__grid">
        {targets.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`aiClerk-att-share__item is-${resolveTone(t)}`}
            onClick={() => handleClick(t)}
            disabled={!shareUrl}
          >
            <div className="aiClerk-att-share__icon">{resolveEmoji(t)}</div>
            <div className="aiClerk-att-share__label">{t.label}</div>
          </button>
        ))}
      </div>
    </div>
  )
}
